"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.intlNumberFormat = exports.formatNumber = exports.formatNumberToAbbr = void 0;
var bignumber_js_1 = __importDefault(require("bignumber.js/bignumber.js"));
var compose_1 = __importDefault(require("lodash/fp/compose"));
var find_1 = __importDefault(require("lodash/fp/find"));
var isString_1 = __importDefault(require("lodash/fp/isString"));
var DEFAULT_FORMAT = {
    groupSeparator: ',',
};
var ONE_THOUSAND = 1000;
var ONE_MILLION = 1000 * 1000;
var ONE_BILLION = 1000 * 1000 * 1000;
function getThresholdUnit(threshold) {
    return {
        1000: 'K',
        1000000: 'M',
        1000000000: 'B',
    }["".concat(threshold)];
}
function parseNumberString(num) {
    return num.replace(/,/g, '');
}
function formatWithUnitSuffix(num, decimalPart) {
    return (0, compose_1.default)(function (unit) { return "".concat(num.dividedBy(unit).toFixed(decimalPart, bignumber_js_1.default.ROUND_FLOOR)).concat(getThresholdUnit(unit)); }, (0, find_1.default)(function (unit) { return num.absoluteValue().isGreaterThanOrEqualTo(unit); }))([ONE_BILLION, ONE_MILLION, ONE_THOUSAND]);
}
/**
 * Format number to abbreviated number.
 *
 * @param options
 * @param options.threshold - Be used to determine whether need to do the abbreviate format.
 *
 * @example
 * ```typescript
 * formatNumberToAbbr()('12345') // '12.34K'
 * formatNumberToAbbr()('12,345,000') // '12.34M'
 * formatNumberToAbbr()('12,345,678,900') // '12.34B'
 * formatNumberToAbbr({ decimalPart: 3, threshold: 1000 * 1000 })(12345) // '12345'
 * formatNumberToAbbr({ decimalPart: 3, threshold: 1000 * 1000 })(123456789) // '123.456M'
 * ```
 * */
var formatNumberToAbbr = function (_a) {
    var _b = _a === void 0 ? {
        decimalPart: 2,
        threshold: ONE_THOUSAND,
    } : _a, decimalPart = _b.decimalPart, threshold = _b.threshold;
    return function (number) {
        var result = new bignumber_js_1.default((0, isString_1.default)(number) ? parseNumberString(number) : number);
        return result.absoluteValue().isGreaterThanOrEqualTo(threshold)
            ? formatWithUnitSuffix(result, decimalPart)
            : "".concat(number);
    };
};
exports.formatNumberToAbbr = formatNumberToAbbr;
/**
 * @example
 * ```typescript
 * formatNumber()(1000.5)
 * // '1,001'
 *
 * formatNumber({
 *   decimalPart: 2,
 *   groupSeparator: ':',
 *   roundUp: true,
 *   keepPositiveSign: true,
 * })(1000.5)
 * // '+1:000.50'
 *
 * formatNumber({ decimalPart: 3 })('123456789000123000456000789')
 * // '123,456,789,000,123,000,456,000,789.000'
 * ```
 * */
var formatNumber = function (_a) {
    if (_a === void 0) { _a = DEFAULT_FORMAT; }
    var _b = _a.decimalPart, decimalPart = _b === void 0 ? 0 : _b, _c = _a.roundUp, roundUp = _c === void 0 ? true : _c, keepPositiveSign = _a.keepPositiveSign, format = __rest(_a, ["decimalPart", "roundUp", "keepPositiveSign"]);
    return function (number) {
        var result = new bignumber_js_1.default(number);
        return "".concat(keepPositiveSign && result.isPositive() ? '+' : '').concat(result.toFormat(decimalPart, roundUp ? bignumber_js_1.default.ROUND_HALF_UP : bignumber_js_1.default.ROUND_DOWN, __assign(__assign({}, format), { decimalSeparator: '.', groupSize: 3 })));
    };
};
exports.formatNumber = formatNumber;
/**
 * Format number depends on locale.
 *
 * @param number - The number need to be format.
 * @param locale - Optional - The target locale should be use.
 *
 * @exampl
 * ```typescript
 * intlNumberFormat(123456.789) // '123,456.789'
 * intlNumberFormat(123456.789, 'en_US') // '123,456.789'
 * intlNumberFormat(123456.789, 'cy_GB') // '123,456.789'
 * intlNumberFormat(123456.789, 'fr_FR') // '123 456,789'
 * ```
 * */
var intlNumberFormat = function (number, locale) {
    var _a;
    var intlLocale = locale || 'en_US';
    var localeStr = intlLocale.replace(/_/g, '-');
    var intlNumberFormatter;
    try {
        intlNumberFormatter = new Intl.NumberFormat(localeStr);
    }
    catch (error) {
        localeStr = (_a = localeStr.split('-')) === null || _a === void 0 ? void 0 : _a[0];
        intlNumberFormatter = new Intl.NumberFormat(localeStr);
    }
    return intlNumberFormatter.format(number);
};
exports.intlNumberFormat = intlNumberFormat;
